import { Select } from "@components/inputs/select";
import { ISelectionChangeArgs, ISelectItem } from "@components/inputs/select/Select.types";
import { IEntity } from "@odata/BindingContext";
import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import { AccountingCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName, getEnumSelectItems } from "@odata/GeneratedEnums.utils";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import Dialog from "../../components/dialog/Dialog";
import Field from "../../components/inputs/field/Field";
import FieldsWrapper from "../../components/inputs/field/FieldsWrapper";
import Input, { IInputOnChangeEvent } from "../../components/inputs/input/Input";
import { AppContext, IAppContext } from "../../contexts/appContext/AppContext.types";
import { BasicInputSizes } from "../../enums";
import { IValidationError } from "../../model/Validator.types";
import ConfirmationButtons from "../../views/table/ConfirmationButtons";
import { createTemplateFromChartOfAccounts } from "./ChartOfAccounts.utils";

interface IProps extends WithTranslation {
    newTemplateName: string;
    templateType?: AccountingCode;
    defaultTemplateType?: AccountingCode;
    onSave: (templateName: string, accountingCode: AccountingCode) => any;
    onClose: () => void;
    setAlert?: (name: string) => void;
    onAfterOpen?: () => void;
    parent: IEntity;
}

interface IState {
    newTemplateName: string;
    templateType: AccountingCode;
    isLoading: boolean;
    error: IValidationError;
}

class NewTemplateDialog extends React.Component<IProps, IState> {
    static contextType = AppContext;
    _isMounted = false;

    constructor(props: IProps) {
        super(props);

        this.state = {
            newTemplateName: "",
            templateType: props.defaultTemplateType ?? AccountingCode.AccountingForBusiness,
            isLoading: false,
            error: null
        }
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    get templateType(): AccountingCode {
        return this.props.templateType ?? this.state.templateType;
    }

    getContext = () => {
        return this.context as IAppContext;
    };

    getTypeItems = (): ISelectItem[] => {
        return getEnumSelectItems(EntityTypeName.Accounting)
            .filter(item => [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations].includes(item.id as AccountingCode))
            .map(item => ({ ...item, label: this.props.t(`ChartsOfAccountsTemplates:AccountingType.${item.id}`) }));
    };

    handleTemplateNameChange = (e: IInputOnChangeEvent<string>): void => {
        this.setState({ error: null, newTemplateName: e.value });
    };

    handleTemplateTypeChange = (args: ISelectionChangeArgs): void => {
        this.setState({ templateType: args.value as AccountingCode });
    };

    handleOnAfterOpen = () => {
        this.setState({ newTemplateName: this.props.newTemplateName });
        this.props.onAfterOpen?.();
    };

    handleSave = async (): Promise<void> => {
        if (!this.state.newTemplateName) {
            this.setState({ error: { message: this.props.t("Error:ValueRequired") } });
            return;
        }
        this.setState({ isLoading: true });
        if (this.props.parent?.Id) {
            try {
                const response = await createTemplateFromChartOfAccounts(this.props.parent.Id, this.state.newTemplateName);
                if (response?.Id) {
                    this.props.setAlert && this.props.setAlert(this.state.newTemplateName);
                    this.props.onSave(this.state.newTemplateName, this.templateType);
                }
            } catch (e) {
                this.setState({ error: { message: this.props.t(`Error:${e?._validationMessages?.[0]?.code}`) } });
            }
        } else {
            const response = await this.props.onSave(this.state.newTemplateName, this.templateType);
            if (response?._validationMessages) {
                const message = this.props.t(`Error:${response?._validationMessages?.[0]?.code}`);
                this.setState({ error: { message } });
            } else if (response) {
                // unknown 500 error
                this.setState({ error: { message: response._message } });
            }
        }

        if (this._isMounted) {
            this.setState({ isLoading: false });
        }
    };

    render() {
        return <Dialog title={this.props.t("ChartsOfAccounts:Table.CreateNewTemplate")}
                       onClose={this.props.onClose}
                       onConfirm={this.handleSave}
                       onAfterOpen={this.handleOnAfterOpen}
                       busy={this.state.isLoading}
                       footer={
                           <ConfirmationButtons
                               confirmText={this.props.t("ChartsOfAccounts:Table.Create")}
                               onCancel={this.props.onClose} onConfirm={this.handleSave}
                               useWrapper={false}/>
                       }>
            {<FieldsWrapper isColumn>
                <Field
                    width={BasicInputSizes.XL}
                    label={this.props.t("ChartsOfAccounts:Table.Name")}
                    isRequired>
                    <Input type="string"
                           width={BasicInputSizes.XL}
                           error={this.state.error}
                           value={this.state.newTemplateName}
                           onChange={this.handleTemplateNameChange}/>
                </Field>
                <Field
                    width={BasicInputSizes.XL}
                    isDisabled={!!this.props.templateType}
                    label={this.props.t("ChartsOfAccountsTemplates:Type")}
                    isRequired>
                    <Select width={BasicInputSizes.XL}
                            items={this.getTypeItems()}
                            isDisabled={!!this.props.templateType}
                            value={this.templateType}
                            onChange={this.handleTemplateTypeChange}/>
                </Field>
            </FieldsWrapper>}
        </Dialog>;
    }
}

export default withTranslation(["ChartsOfAccounts", "ChartsOfAccountsTemplates", getEnumNameSpaceName(EntityTypeName.Accounting), "Common", "Error"])(NewTemplateDialog);